
import { create } from 'zustand';
import type { Product, Category, ProductFilter } from '../types';
import { apiClient } from '../lib/api';

interface ProductsState {
  products: Product[];
  categories: Category[];
  selectedCategory: string | null;
  searchTerm: string;
  searchType: ProductFilter['searchType'];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchProducts: () => Promise<void>;
  fetchCategories: () => Promise<void>;
  setSelectedCategory: (categoryId: string | null) => void;
  setSearchTerm: (term: string) => void;
  setSearchType: (type: ProductFilter['searchType']) => void;
  getFilteredProducts: () => Product[];
  searchProductByBarcode: (barcode: string) => Product | null;
}

export const useProductsStore = create<ProductsState>((set, get) => ({
  products: [],
  categories: [],
  selectedCategory: null,
  searchTerm: '',
  searchType: 'name',
  isLoading: false,
  error: null,

  fetchProducts: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get('/api/products?is_active=true&limit=1000');
      const productsData = response.data?.data ?? response.data;
      set({ products: Array.isArray(productsData) ? productsData : [], isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch products',
        isLoading: false
      });
    }
  },

  fetchCategories: async () => {
    try {
      const response = await apiClient.get('/api/categories?is_active=true');
      const categoriesData = response.data?.data ?? response.data;
      set({ categories: Array.isArray(categoriesData) ? categoriesData : [] });
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  },

  setSelectedCategory: (categoryId) => {
    set({ selectedCategory: categoryId });
  },

  setSearchTerm: (term) => {
    set({ searchTerm: term });
  },

  setSearchType: (type) => {
    set({ searchType: type });
  },

  getFilteredProducts: () => {
    const { products, selectedCategory, searchTerm, searchType } = get();
    
    let filtered = products.filter(p => p.is_active);

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(p => p.category_id === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      
      filtered = filtered.filter(product => {
        switch (searchType) {
          case 'barcode':
            return product.barcode?.toLowerCase().includes(term);
          case 'code':
            return product.code.toLowerCase().includes(term);
          case 'name':
            return product.name.toLowerCase().includes(term);
          case 'wildcard':
            return (
              product.name.toLowerCase().includes(term) ||
              product.code.toLowerCase().includes(term) ||
              product.barcode?.toLowerCase().includes(term)
            );
          default:
            return true;
        }
      });
    }

    return filtered;
  },

  searchProductByBarcode: (barcode) => {
    const { products } = get();
    return products.find(p => p.barcode === barcode && p.is_active) || null;
  },
}));
